import { Error } from "@components/Error";
import {
  ILSHeaderPlaceholder,
  ILSParagraphPlaceholder,
} from "@components/ILSPlaceholder";
import { invenioConfig } from "@config";
import _isEmpty from "lodash/isEmpty";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Container, Header } from "semantic-ui-react";
import DashboardHeader from "../Dashboard/DashboardHeader";

class StaticPage extends Component {
  constructor(props) {
    super(props);
    this.state = {
      data: {},
      isLoading: true,
      error: null,
    };
  }

  componentDidMount() {
    this.fetchPageByRoute();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.location.pathname !== this.props.location.pathname) {
      this.fetchPageByRoute();
    }
  }

  fetchPageByRoute = async () => {
    const { location } = this.props;
    const currentRoute = location.pathname;
    const routeToIdMap = {
      "/about": 1,
      "/contact": 2,
      "/guide/search": 3,
      "/download": 4,
      "/eresources": 5,
      "/library-policies": 6,
    };
    const pageId = routeToIdMap[currentRoute];
    if (!pageId) {
      this.setState({
        data: {},
        isLoading: false,
        error: { message: "Page not found" },
      });
      return;
    }
    this.setState({ isLoading: true, error: null });
    try {
      const apiUrl = `${invenioConfig.APP.REST_ENDPOINTS_BASE_URL}/pages/${pageId}`;
      const response = await fetch(apiUrl, {
        method: "GET",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const pageData = await response.json();
      if (!pageData || typeof pageData !== "object") {
        throw new Error("Invalid page data received");
      }
      this.setState({
        data: pageData,
        isLoading: false,
        error: null,
      });
    } catch (err) {
      this.setState({
        data: {},
        isLoading: false,
        error: {
          message: `Failed to load page: ${err.message}`,
          originalError: err,
        },
      });
    }
  };

  parseStaticPageContent = () => {
    const { data } = this.state;
    if (!_isEmpty(data) && data.content) {
      return (
        <div
          dangerouslySetInnerHTML={{ __html: data.content }}
          className="static-page-content"
        />
      );
    }
    return (
      <div className="no-content">
        <p>No content available for this page.</p>
      </div>
    );
  };

  getPageTitle = () => {
    const { data, isLoading } = this.state;
    const { location } = this.props;
    if (!isLoading && !_isEmpty(data) && data.title) {
      return data.title;
    }
    const fallbackTitles = {
      "/about": "About Us",
      "/contact": "Contact Information",
      "/guide/search": "Search Guide",
      "/download": "Downloads",
      "/eresources": "Electronic Resources",
      "/library-policies": "Library Policies",
    };
    return fallbackTitles[location.pathname] || "Page";
  };

  render() {
    const { isLoading, error } = this.state;
    const pageTitle = this.getPageTitle();
    const { location } = this.props;

    // Map routes to activeTab values
    const routeToTabMap = {
      "/about": "about",
      "/contact": "contact",
      "/eresources": "e-resources",
      "/library-policies": "library-policies",
      // Add other routes as needed
    };

    const activeTab = routeToTabMap[location.pathname] || "advisory";

    return (
      <Overridable id="StaticPage.layout">
        <Error boundary error={error}>
          <DashboardHeader activeTab={activeTab} />
          <Container style={{ marginTop: "6rem", marginBottom: "2rem" }}>
            <ILSHeaderPlaceholder fluid isLoading={isLoading}>
              <Header as="h1" className="page-title">
                {pageTitle}
              </Header>
            </ILSHeaderPlaceholder>
            <ILSParagraphPlaceholder
              fluid
              isLoading={isLoading}
              linesNumber={30}
              style={{ marginTop: "2rem" }}
            >
              {!isLoading && !error && this.parseStaticPageContent()}
              {!isLoading && error && (
                <div className="error-message">
                  <Header as="h3" color="red">
                    Error Loading Page
                  </Header>
                  <p>{error.message}</p>
                </div>
              )}
            </ILSParagraphPlaceholder>
          </Container>
        </Error>
      </Overridable>
    );
  }
}

StaticPage.propTypes = {
  match: PropTypes.shape({
    path: PropTypes.string,
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
};

StaticPage.defaultProps = {
  match: {
    path: "",
  },
};

export default Overridable.component("StaticPage", StaticPage);
